import {
  type Address,
  type CartItem,
} from "@paparazzi/types/paparazzi.api.serializers";
import type { Rep } from "@paparazzi/types/paparazzi.api.serializers.profiles";
import type { SignupItem } from "@paparazzi/types/paparazzi.api.viewsets.signup_items";
import type { AuthorizeCreditCard } from "@paparazzi/types/paparazzi.authorizecim.views";
import api from "@virgodev/bazaar/functions/api";
import copy from "@virgodev/bazaar/functions/copy";
import { persist } from "@virgodev/vue-models/utils/persist";
import { defineStore } from "pinia";
import sortBy from "sort-by";
import { computed, ref, watch } from "vue";
import { useCartStore } from "./cart";

interface SignupTotalItem {
  InventoryID: number;
  ProductID: string;
  Price: number;
  Tax: number;
  Quantity: number;
}

interface SignupTotals {
  Items: SignupTotalItem[];
  ShippingTotal: number;
  ShippingTax: number;
  TaxTotal: number;
  HandlingFee: number;
  HandlingTax: number;
  Total: number;
  CreditAmount: number;
  BalanceDue: number;
}

interface CartItemWithVolume extends CartItem {
  volume?: number;
}

export const useJoinStore = defineStore("join", () => {
  const cart = useCartStore();

  const kits = ref([] as SignupItem[]);

  const info = ref({
    kit: 0,
    rep: undefined as Rep | undefined,
    accountInfoComplete: false,

    // sections of account forms
    account: false,
    payout: false,
    password: false,
  });

  const methodOptions = ref([
    { value: "2", name: "Direct Deposit" },
    { value: "1", name: "Mail Physical Check" },
  ]);
  const accountTypeOptions = ref([
    { value: "2", name: "Checking" },
    { value: "3", name: "Savings" },
  ]);

  const accountData = ref({
    first_name: "",
    last_name: "",
    dob: "",
    ssn: "",
    email: "",
    phone: "",
    language: "1",
  });
  const addressData = ref({
    address: "",
    address2: "",
    city: "",
    state: "",
    county: "",
    zip_code: "",
  } as Address);
  const payoutData = ref({
    payout_method: methodOptions.value[0].value,
    bank_name: "",
    routing_number: "",
    account_number: "",
    account_type: accountTypeOptions.value[0].value,
  });
  const passwordData = ref({
    back_office_password: "",
    confirm_password: "",
  });
  const flagData = ref({
    terms_agreed: false,
    send_email: false,
  });
  const fees = ref([]);
  const errors = ref<{ [key: string]: string[] }>({});
  const totals = ref<SignupTotals>();
  const card = ref<Partial<AuthorizeCreditCard>>({});
  const cvc = ref("");

  persist(
    {
      join_info: info,
      join_account: accountData,
      join_address: addressData,
    },
    {
      expireAfter: 1000 * 60 * 30,
      purgeKeys: ["ssn"],
    },
  );

  const data = computed(() => {
    return {
      ...addressData,
      ...payoutData,
      ...passwordData,
      ...flagData,
    };
  });

  const kit = computed<SignupItem | undefined>(() => {
    return kits.value.find((k) => k.id === info.value.kit);
  });

  const payoutFields = computed(() => {
    let fields: any = [
      {
        name: "payout_method",
        label: "Payout Method",
        options: methodOptions.value,
        default: methodOptions.value[0].value,
        required: true,
      },
    ];

    let fieldsToAdd;

    if (payoutData.value.payout_method === "2") {
      fieldsToAdd = [
        { name: "bank_name", label: "Bank Name", type: "text", required: true },
        {
          label: false,
          type: "sub",
          css_classes: "hrow",
          fields: [
            {
              name: "routing_number",
              label: "Routing Number",
              type: "text",
              required: true,
              css_classes: "_lr-hide",
            },
            {
              name: "account_number",
              label: "Account Number",
              type: "text",
              required: true,
              css_classes: "_lr-hide",
            },
          ],
        },
        {
          name: "account_type",
          label: "Account Type",
          options: accountTypeOptions.value,
          default: accountTypeOptions.value[0].value,
          required: true,
          css_classes: "_lr-hide",
        },
      ];
    }

    if (fieldsToAdd) {
      fieldsToAdd.forEach((field) => {
        fields.push(field);
      });
    }

    return fields;
  });

  const shippingMethod = computed(() => {
    if ((kit.value?.jewelry_count || 0) > 0) {
      return 1029;
    } else {
      return 1038;
    }
  });

  const count = computed(() => {
    let items = 0;
    for (const item of cart.items.filter((i) => !i.promotion)) {
      const qtyScale = parseInt(item.product?.volume ?? "0") / 2;
      items += item.quantity * qtyScale;
    }
    return items;
  });

  const extraItems = computed<CartItem[]>(() => {
    const items = [];
    let count = kit.value?.jewelry_count || 0;
    const cartitems: CartItemWithVolume[] = cart.items.filter(
      (i) => !i.promotion,
    );
    cartitems.forEach(
      (item) => (item.volume = parseFloat(item.product?.volume ?? "0")),
    );
    cartitems.sort(sortBy("-volume"));
    for (const item of cartitems) {
      const volume = parseFloat(item.product?.volume ?? "0");
      if (volume > 0) {
        const qtyScale = Math.floor(volume / 2);
        const qty = item.quantity;
        if (qty * qtyScale <= count) {
          // decrement free items
          count -= qty * qtyScale;
        } else if (count > qtyScale) {
          const copied = copy(item);
          copied.quantity = Math.floor(item.quantity - count / qtyScale);

          // partially free
          items.push(copied);
          count = 0;
        } else {
          // no free items remain
          items.push(item);
        }
      } else {
        // non-pv item
        items.push(item);
      }
    }
    return items;
  });

  const extraItemCount = computed<number>(() => {
    return extraItems.value.reduce((acc, item) => {
      const volume = parseFloat(item.product?.volume ?? "0");
      const qtyScale = Math.floor(volume / 2);
      return acc + item.quantity * qtyScale;
    }, 0);
  });

  watch(
    [() => cart.object.items, addressData, () => info.value.kit],
    () => (totals.value = undefined),
    {
      deep: true,
    },
  );

  async function getStarterKits() {
    const response = await api({ url: "signup_items/" });
    if (response.ok) {
      kits.value = response.body.results;
    }
  }

  function encryptedAccountNumber() {
    const dotsCount = payoutData.value.account_number.length - 4;
    let encryptedString = "";

    for (let i = 0; i < dotsCount; i++) {
      encryptedString += "*";
    }

    const lastFour = payoutData.value.account_number.slice(-4);
    return encryptedString + lastFour;
  }

  async function isReady(): Promise<boolean> {
    return !!accountData.value.ssn && !!passwordData.value.back_office_password;
  }

  /** verify a rep is good to go and get checkout data */
  async function check(): Promise<boolean> {
    const cart = useCartStore();
    errors.value = {};
    const response = await api({
      url: "signup/",
      params: {
        ship_city: addressData.value.city,
        ship_state: addressData.value.state,
        ship_county: addressData.value.county ?? "",
        ship_postal_code: addressData.value.zip_code,
        starter_kit_id: kit.value?.remote_id || "",
        consultant_id: info.value.rep?.rep_number || "",
        shipping: shippingMethod.value,
        customItems: JSON.stringify(
          cart.items
            .filter((i) => !i.promotion)
            .map((item) => ({
              product: item.product?.remote_id,
              quantity: item.quantity,
            })),
        ),
      },
    });
    if (response.ok) {
      fees.value = response.body.fees;
      totals.value = response.body.totals;
    } else {
      errors.value.__all__ = [response.body.error];
      totals.value = undefined;
      fees.value = [];
    }
    return response.ok;
  }

  async function start(): Promise<{ order: number; consultant: number }> {
    const cart = useCartStore();
    errors.value = {};
    const response = await api({
      url: "signup/",
      method: "POST",
      json: {
        starter_kit_id: kit.value?.remote_id,
        consultant_id: info.value.rep?.rep_number || "",
        shipping: shippingMethod.value,
        first_name: accountData.value.first_name,
        last_name: accountData.value.last_name,
        date_of_birth: accountData.value.dob,
        social_security_number: accountData.value.ssn,
        // company: "ACME, Inc.",
        preferred_language: accountData.value.language,
        bill_street_1: card.value.billing_address?.address,
        bill_street_2: card.value.billing_address?.address2,
        bill_postal_code: card.value.billing_address?.zip_code,
        bill_city: card.value.billing_address?.city,
        bill_state: card.value.billing_address?.state,
        bill_county: card.value.billing_address?.county,
        bill_country: "USA",
        ship_street_1: addressData.value.address,
        ship_street_2: addressData.value.address2,
        ship_postal_code: addressData.value.zip_code,
        ship_city: addressData.value.city,
        ship_state: addressData.value.state,
        ship_county: addressData.value.county,
        ship_country: "USA",
        email: accountData.value.email,
        home_phone: accountData.value.phone,
        // cell_phone: "",
        // work_phone: "",
        // fax_number: "435-555-0003",
        payout_method: payoutData.value.payout_method,
        bank_name: payoutData.value.bank_name,
        routing_number: payoutData.value.routing_number,
        account_number: payoutData.value.account_number,
        account_type: payoutData.value.account_type,
        password1: passwordData.value.back_office_password,
        password2: passwordData.value.confirm_password,
        agree: "on",
        shipping_method: shippingMethod.value,
        card: {
          first_name: card.value.first_name,
          last_name: card.value.last_name,
          credit_card: card.value.card,
          secure_code: cvc.value,
          expiration: `${card.value.month}/${card.value.year?.slice(-3)}`,
        },
        total: totals.value?.Total,
        customItems: cart.items
          .filter((i) => !i.promotion)
          .map((item) => ({
            product: item.product?.remote_id,
            quantity: item.quantity,
          })),
        // TODO
        // { product: "D101", quantity: 1 }
        // ],
      },
    });
    if (response.ok) {
      if (response.body.errors) {
        errors.value = response.body.errors;
      } else if (response.body.order) {
        console.log(" >> signup success!", response.body);

        return {
          order: response.body.order,
          consultant: response.body.rep_number,
        };
      } else {
        errors.value.__all__ = [
          "Error processing request, please contact support",
        ];
      }
    } else {
      errors.value.__all__ = [response.body];
    }
    return {
      order: 0,
      consultant: 0,
    };
  }

  function clear() {
    cvc.value = "";
    card.value = {};
    totals.value = undefined;
    errors.value = {};
    fees.value = [];
    flagData.value.terms_agreed = false;
    flagData.value.send_email = false;
    passwordData.value.back_office_password = "";
    passwordData.value.confirm_password = "";
    payoutData.value.payout_method = methodOptions.value[0].value;
    payoutData.value.bank_name = "";
    payoutData.value.routing_number = "";
    payoutData.value.account_number = "";
    payoutData.value.account_type = accountTypeOptions.value[0].value;
    addressData.value.address = "";
    addressData.value.address2 = "";
    addressData.value.city = "";
    addressData.value.state = "";
    addressData.value.county = "";
    addressData.value.zip_code = "";
    accountData.value.first_name = "";
    accountData.value.last_name = "";
    accountData.value.dob = "";
    accountData.value.ssn = "";
    accountData.value.email = "";
    accountData.value.phone = "";
    accountData.value.language = "1";
    info.value.kit = 0;
    info.value.rep = undefined as Rep | undefined;
    info.value.accountInfoComplete = false;
    info.value.account = false;
    info.value.payout = false;
    info.value.password = false;
  }

  return {
    errors,
    totals,
    fees,
    info,
    kits,
    kit,
    card,
    cvc,
    count,
    extraItems,
    extraItemCount,
    payoutFields,
    accountData,
    addressData,
    payoutData,
    passwordData,
    flagData,
    data,
    methodOptions,
    getStarterKits,
    encryptedAccountNumber,
    isReady,
    check,
    start,
    clear,
  };
});
